import { CommonModule } from '@angular/common';
import { HttpClient } from "@angular/common/http";
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { DateRange, DateUtil } from "@qid/core";
import { SexyModule } from '@qid/sexy';
import { SuperButtonComponent } from '@qid/super';
import Chart from 'chart.js/auto';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import { firstValueFrom } from 'rxjs';
import { ScannerTemplateOptions } from "../../models/scanner-template-options";
import { OrganisationRelationshipFacade } from "../../modules/organisation-relationship";
import { ShareService } from "../../modules/share";
import { TemplateSelectorComponent } from '../../components/template-selector/template-selector.component';
import { FormsModule } from '@angular/forms';
import { IonHeader, IonToolbar, IonTitle, IonContent, IonIcon, IonSegment, IonSegmentButton, IonLabel, IonItem, IonToggle } from '@ionic/angular/standalone';

@Component({
  selector: 'oneqid-nx-view-share-analytics',
  templateUrl: './view-share-analytics.page.html',
  styleUrls: ['./view-share-analytics.page.scss'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    SexyModule,
    SuperButtonComponent,
    TemplateSelectorComponent,
    IonHeader,
    IonToolbar,
    IonTitle,
    IonContent,
    IonIcon,
    IonSegment,
    IonSegmentButton,
    IonLabel,
    IonItem,
    IonToggle
  ],
})
export class ViewShareAnalyticsPage implements OnInit {
  @ViewChild('count-chart-canvas') countChartCanvas: ElementRef;

  analyticsData = null;
  templateOptions: ScannerTemplateOptions[] = [];

  countChart = null;
  ageChart = null;
  genderChart = null;

  granularity = 'day'

  orgRelationship$ = this.organisationRelationshipFacade.childOrganisations$

  showBranchAccounts: boolean = false;

  constructor(private shareService: ShareService,
    private http: HttpClient,
    private organisationRelationshipFacade: OrganisationRelationshipFacade,
  ) {

  }

  countChartData = {
    gradient: null,
    width: null,
    height: null,
  }
  isAllTemplateSelected: boolean = true;

  public selectedRangeOption = DateRange.WEEK;

  ngOnInit() {

    this.organisationRelationshipFacade.initParentBranch()

  }

  ionViewDidEnter() {
    this.fetchAnalytics()
  }

  fetchAnalytics = async () => {

    const range = DateUtil.getDateRange(this.selectedRangeOption);

    let childAccountIds = []
    const relationsShips = await firstValueFrom(this.orgRelationship$)

    if (relationsShips) {
      childAccountIds = relationsShips.map(relation => relation.childOrg._id)
    }

    const res = await this.shareService.fetchAnalytics(
      [],
      range.startDate,
      range.endDate,
      this.granularity, this.showBranchAccounts
    )

    if (res?.data) {
      this.analyticsData = res.data
      this.loadCharts()
    }
  }

  loadCharts() {
    this.countChartData = {
      gradient: null,
      width: null,
      height: null,
    }
    this.loadGenderChart();
    this.loadCountChart();
    this.loadAgeChart();
  }

  loadGenderChart() {
    // Ensure data from API is available
    const genderStats = this.analyticsData.genderStats || [];

    // Extract labels, values, and colors dynamically
    const labels = genderStats.map(stat => stat._id || 'Unknown');
    const data = genderStats.map(stat => stat.count || 0);
    const backgroundColors = ['rgb(0,0,0)', 'rgb(40,40,40)', 'rgb(126,126,126)'];

    if (this.genderChart) {
      // Update the existing chart with new data
      this.genderChart.data.labels = labels;
      this.genderChart.data.datasets[0].data = data;
      this.genderChart.update();
      return;
    }

    this.genderChart = new Chart('gender-chart-canvas', {
      type: 'doughnut',
      data: {
        labels,
        datasets: [{
          data,
          backgroundColor: backgroundColors,
          hoverOffset: 4,
        }],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            bottom: 20, // Add 20px padding at the bottom
          }
        },
        plugins: {
          legend: { display: false },
          datalabels: {
            color: '#646464',
            font: { size: 12 },
            formatter: (value, context) => {
              return context.chart.data.labels[context.dataIndex];
            },
            anchor: 'end',
            align: 'end',
            offset: -2, // Space around labels
          },
        },
      },
      plugins: [ChartDataLabels], // Add the plugin
    });
  }




  loadCountChart() {
    const data = this.analyticsData.countData;
    const labels = data.map(item => item.time);
    const counts = data.map(item => item.count);

    const chatData: any = {
      labels: labels, // The weeks/months
      datasets: [{
        data: counts,   // Data for the count (y-axis values)
        backgroundColor: (context) => {
          const chart = context.chart;
          const { ctx, chartArea } = chart;
          if (!chartArea) {
            // This case happens on initial chart load
            return;
          }
          return this.getGradient(ctx, chartArea);
        },
        borderColor: 'rgb(12,12,12)', // Border color
        borderWidth: 2,
        fill: true,
        tension: 0.4,
      }]
    }

    if (this.countChart) {
      this.countChart.data.labels = labels;
      this.countChart.data.datasets[0].data = counts;
      this.countChart.update();
      return;
    }

    this.countChart = new Chart('count-chart-canvas', {
      type: 'line',
      data: chatData,
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          x: {
            title: {
              display: false,
            },
            grid: {
              display: false
            }
          },
          y: {
            border: {
              display: false
            },
            title: {
              display: false
            },
            ticks: {
              display: false
            },
            beginAtZero: true,
            grid: {
              display: false
            }
          },
        },
        plugins: {
          legend: {
            display: false
          }
        }
      },
    });
  }

  getGradient(ctx, chartArea) {
    const chartWidth = chartArea.right - chartArea.left;
    const chartHeight = chartArea.bottom - chartArea.top;
    if (!this.countChartData.gradient || this.countChartData.width != chartWidth || this.countChartData.height != chartHeight) {
      this.countChartData.width = chartWidth;
      this.countChartData.height = chartHeight;
      this.countChartData.gradient = ctx.createLinearGradient(0, chartArea.bottom, 0, chartArea.top);
      this.countChartData.gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
      this.countChartData.gradient.addColorStop(0, 'rgba(16,16,16,0.8)');  // Blue at the top
      this.countChartData.gradient.addColorStop(1, 'rgba(26,26,26,0)');    // Transparent at the bottom

    }
    return this.countChartData.gradient;
  }


  loadAgeChart() {
    const ageStats = this.analyticsData.ageStats;

    const labels = ['0-18', '19-24', '25-34', '35-40', '41+', "Unknown"];
    const bandMap = Object.fromEntries(ageStats.map(item => [item._id, item.count]));
    const data = labels.map(label => bandMap[label] || 0);

    if (this.ageChart) {
      // Recalculate total whenever chart data updates
      const total = data.reduce((sum, count) => sum + count, 0);

      this.ageChart.data.labels = labels;
      this.ageChart.data.datasets[0].data = data;

      // Update tooltip callback to reflect the new total
      this.ageChart.options.plugins.tooltip.callbacks.label = function (tooltipItem) {
        const count = tooltipItem.raw;
        const percentage = ((count / total) * 100).toFixed(2);
        return `${percentage}%`;
      };

      this.ageChart.update();
      return;
    }

    // Calculate total initially
    const total = data.reduce((sum, count) => sum + count, 0);

    const chartData = {
      labels: labels,
      datasets: [{
        label: 'Participants by Age Group',
        data: data,
        backgroundColor: ['#212121'],
        borderRadius: 10,
      }]
    };

    this.ageChart = new Chart('age-chart-canvas', {
      type: 'bar',
      data: chartData,
      options: {
        responsive: true,
        plugins: {
          legend: {
            display: false
          },
          tooltip: {
            callbacks: {
              label: function (tooltipItem) {
                const count: any = tooltipItem.raw;
                const percentage = ((count / total) * 100).toFixed(2);
                return `${percentage}%`;
              }
            }
          }
        },
        elements: {
          bar: {
            inflateAmount: 2
          }
        },
        scales: {
          x: {
            title: {
              display: false,
            },
            grid: {
              display: false
            }
          },
          y: {
            display: false,
            title: {
              display: false,
            },
            grid: {
              display: false
            },
            beginAtZero: true
          }
        }
      }
    });
  }

  onDateRangeChanged() {
    this.fetchAnalytics()
  }


  protected readonly DateRange = DateRange;
  protected readonly alert = alert;


  onBranchesToggled() {
    this.fetchAnalytics()
  }
}

